/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { required } from '@src/app/shared/validators';
import { DocumentService } from '@src/app/modules/crm/document.service';
import { Document } from '@src/app/model/document';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { NewButtonService } from '@src/app/shared/new-button/new-button.service';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { Router, RouterLink } from '@angular/router';
import { User } from '@src/app/model/user';
import { MatButtonModule } from '@angular/material/button';
import { Field } from '@src/app/shared/field/Field';
import { ChannelPickerComponent } from '@src/app/modules/social/channel-picker/channel-picker.component';
import { uuid } from '@src/app/model/abstract-object';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { DocumentPageNavRoute } from '@src/app/routes/app-router';
import { TenantService } from '@src/app/shared/tenant.service';

@Component({
    selector: 'app-document-page',
    templateUrl: './document-page.component.html',
    styleUrls: ['./document-page.component.scss'],
    imports: [FormPageComponent, RouterLink, MatButtonModule, ChannelPickerComponent]
})
export class DocumentPageComponent extends AbstractPageComponent {
    dataSvc = inject(DocumentService);
    private nbs = inject(NewButtonService);
    private currentUserSvc = inject(CurrentUserService);
    private router = inject(Router);
    private screenSvc = inject(IsNarrowService);
    public tenantSvc = inject(TenantService);


    currentChannelFilter: uuid;

    askSuggestions = false;

    forumField = FormPicklistComponent.make('Access Channel', 'forumId', 'forum',
        { items: this.currentUserSvc.getForums() },
        { validators: [required], cellOpts: { width: '10%' }, visible: Field.noPhone }
    );

    private destroyRef = inject(DestroyRef);

    constructor() {
        super();
        this.currentUserSvc.getCurrentUser().pipe(takeUntilDestroyed()).subscribe(u => {
            const dp = User.getModelPrivilege(u, 'Document');
            this.config.docPath = dp.put
                ? 'omc-community/managers/documents'
                : 'omc-community/owners-residents/documents';
        });
    }

    config = new FormConfig({
        navRoute: DocumentPageNavRoute,
        title: $localize`Documents`,
        help: $localize`Documentation owned and managed by the OMC`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                FormTextComponent.make('Title', 'title', { validators: [required],
                    cellOpts: { width: this.screenSvc.isPhone() ? '100%' : '20%'} }
                ),
                this.forumField,
                FormTextComponent.make('Area', 'areaName', {visible: Field.noPhone}),
                FormTextComponent.make('Core', 'coreName', { visible: Field.noPhone }),
                FormTextComponent.make('Type', 'typeName', {visible: Field.noPhone}),
                FormTextComponent.make('Category', 'categoryName', { visible: Field.noPhone }),                
                FormNumberComponent.make('Items', 'child_docs_count', {}, {
                    sendServer: false, disable: true, visible: Field.noPhone, cellOpts: { width: '5%', style: 'text-align:center' }
                }),
                FormNumberComponent.make('Files', 'attachments_count', {}, { sendServer: false, disable: true,
                    visible: Field.noPhone, cellOpts: {
                    width: '5%', style: 'text-align:center',
                }}),
                FormRichTextComponent.make('Content', 'content', { visible: Field.noPhone, cellOpts: {width:"80%"} } ),
            ],
            formLayout: LAYOUT_OPTIONS.singleCol,
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: () => of(new Document()),
        newOptions: [],
        beforeList: this.beforeList.bind(this),
    });

    beforeList(data: Document[], parms) {
        if (parms.map.has('forumId')) {
            console.log('setting Filtering by channel: ' + parms.map.get('forumId'));
            this.currentChannelFilter = parms.map.get('forumId').toString();
        } else {
            console.log('clearing Filtering by channel: ');
            this.currentChannelFilter = null;
            this.forumField.filter.active = false;
        }
        if (data.length < 3) {
            this.currentUserSvc.getCurrentUser().pipe(takeUntilDestroyed(this.destroyRef)).subscribe ( u => {
                if (User.canPostOwnSR(u)) {
                    this.askSuggestions = true;
                }
            })
        } else {
            this.askSuggestions = false;
        }

        return data.sort( (a,b) => a.title.localeCompare(b.title));
    }

    filterByChannel(channelId) {
        //this.currentChannelFilter = channelId;
        if (channelId) {
            this.router.navigate([DocumentPageNavRoute.getListRouteName(), { forumId: channelId }])
        } else {
            this.router.navigate([DocumentPageNavRoute.getListRouteName()])
        }
    }

}
